import type { AudioRhythmColorId, AudioRhythmMode, AudioRhythmSettings, Rgb } from './types'

/**
 * 灯效模式列表（供 web 层「灯光模式」选择 UI 渲染）。
 * - id：模式标识，传给 AudioRhythmEngine.renderFrame 的 mode 参数
 * - label：中文显示名
 * - icon：UI 图标名
 */
export const AUDIO_RHYTHM_MODES: {
  id: AudioRhythmMode
  label: string
  icon: 'bars' | 'pulse' | 'peak' | 'scan' | 'wave' | 'flame' | 'beat'
}[] = [
  { id: 'spectrumBar', label: '频谱柱', icon: 'bars' },
  { id: 'centralPulse', label: '中央脉冲', icon: 'pulse' },
  { id: 'peakMeter', label: '峰值仪', icon: 'peak' },
  { id: 'scanLine', label: '扫描线', icon: 'scan' },
  { id: 'wave', label: '波浪', icon: 'wave' },
  { id: 'flame', label: '火焰', icon: 'flame' },
  { id: 'beatBreathe', label: '节拍呼吸', icon: 'beat' },
]

/** 颜色渐变停靠点。 */
export interface ColorStop {
  /** 停靠点位置 0..1。 */
  t: number
  /** 该位置的 RGB 颜色。 */
  color: Rgb
}

/**
 * 颜色预设列表（供 web 层「颜色」选择 UI 渲染）。
 * - id：颜色标识，传给 AudioRhythmEngine.renderFrame 的 colorId 参数
 * - label：中文显示名
 * - stops：渐变停靠点（颜色按 t 插值）
 */
export const AUDIO_RHYTHM_COLORS: {
  id: AudioRhythmColorId
  label: string
  stops: ColorStop[]
}[] = [
  {
    id: 'neonCyan',
    label: '霓虹青',
    // 中心亮绿、边缘深青蓝：扩大色相跨度（绿→蓝），
    // 让真机 LED（蓝通道偏强、且 1.8× 增益会饱和高亮通道）也能看出从中心到边缘的过渡，
    // 避免两端色相过近（原 0/255/120 与 0/220/255）在灯珠上被压成同一种「靛蓝绿」。
    stops: [
      { t: 0, color: { r: 0, g: 255, b: 70 } },
      { t: 0.5, color: { r: 0, g: 180, b: 170 } },
      { t: 1, color: { r: 0, g: 70, b: 255 } },
    ],
  },
  {
    id: 'neonBlue',
    label: '霓虹蓝',
    stops: [
      { t: 0, color: { r: 30, g: 80, b: 255 } },
      { t: 1, color: { r: 160, g: 40, b: 255 } },
    ],
  },
  {
    id: 'electricArc',
    label: '电弧',
    // 白热主导、蓝仅点缀：芯部白中透冷调（≈246/252/252），
    // 中段为低饱和冰蓝（≈224/243/250，避免纯青 0/248/252 那种"太蓝"），
    // 边缘钢蓝收尾（≈126/198/246）。
    stops: [
      { t: 0, color: { r: 200, g: 210, b: 245 } },
      { t: 0.5, color: { r: 140, g: 180, b: 230 } },
      { t: 1, color: { r: 60, g: 100, b: 190 } },
    ],
  },
  {
    id: 'neonPurple',
    label: '霓虹紫',
    // 中心保持高饱和蓝紫（170/40/255 增益后≈238/72/252，真机中间呈现紫色）；
    // 关键在 R 通道：中段/外圈若 R 与 B 同时偏高（如 R≈225、B≈252），
    // 经 1.8× 增益+软饱和后两者被压到一起，混成偏蓝白光。
    // 因此 R 从中段起快速衰减、B 同步微降，外圈为深蓝紫而非蓝白光。
    stops: [
      { t: 0, color: { r: 170, g: 40, b: 255 } },
      { t: 0.5, color: { r: 80, g: 20, b: 200 } },
      { t: 1, color: { r: 40, g: 12, b: 150 } },
    ],
  },
  {
    id: 'neonPink',
    label: '霓虹粉',
    stops: [
      { t: 0, color: { r: 220, g: 20, b: 140 } },
      { t: 1, color: { r: 255, g: 80, b: 140 } },
    ],
  },
  {
    id: 'flame',
    label: '烈焰',
    // 橙红芯（增益后≈252/162/54）→ 深红边（≈243/18/0），与霓虹橙区分开
    stops: [
      { t: 0, color: { r: 255, g: 90, b: 30 } },
      { t: 1, color: { r: 180, g: 10, b: 0 } },
    ],
  },
  {
    id: 'neonGreen',
    label: '霓虹绿',
    stops: [
      { t: 0, color: { r: 160, g: 255, b: 40 } },
      { t: 1, color: { r: 20, g: 100, b: 40 } },
    ],
  },
  {
    id: 'neonYellow',
    label: '霓虹黄',
    // 柠檬黄芯（增益后≈252/252/144）→ 琥珀边（≈252/236/0）
    stops: [
      { t: 0, color: { r: 255, g: 245, b: 80 } },
      { t: 1, color: { r: 255, g: 165, b: 0 } },
    ],
  },
  {
    id: 'neonOrange',
    label: '霓虹橙',
    // 纯橙（增益后≈252/216/0，无黄无红）→ 深橙（≈246/108/0）
    stops: [
      { t: 0, color: { r: 255, g: 120, b: 0 } },
      { t: 1, color: { r: 200, g: 60, b: 0 } },
    ],
  },
  {
    id: 'rainbow',
    label: '彩虹',
    stops: [
      { t: 0, color: { r: 255, g: 0, b: 0 } },
      { t: 0.17, color: { r: 255, g: 160, b: 0 } },
      { t: 0.33, color: { r: 255, g: 255, b: 0 } },
      { t: 0.5, color: { r: 0, g: 220, b: 80 } },
      { t: 0.67, color: { r: 0, g: 160, b: 255 } },
      { t: 0.83, color: { r: 120, g: 40, b: 255 } },
      { t: 1, color: { r: 255, g: 0, b: 180 } },
    ],
  },
]

/** 默认灯效设置（web 层初始化设置面板时使用）。 */
export const DEFAULT_AUDIO_RHYTHM_SETTINGS: AudioRhythmSettings = {
  lowSensitivity: 100,
  midSensitivity: 100,
  highSensitivity: 100,
  brightness: 100,
  trailSmoothing: 50,
}

/**
 * 频谱柱列数（键盘按水平方向切分的频谱列，频谱柱/峰值仪等效果使用）。
 * 传给 AudioRhythmEngine 构造的 columnCount 参数。
 */
export const SPECTRUM_COLUMN_COUNT = 15

/**
 * FFT 采样点数（web 层音频采集时设置 AnalyserNode.fftSize 用）。
 * 必须与 SDK 内部 extractBands 使用的 FFT_SIZE 保持一致，否则频段划分错位。
 */
export const FFT_SIZE = 2048
